import moment from "moment";
import React, {Fragment} from "react";
import {PredictionResult, PlayerImageType} from "modules";
import {PlayerImage} from "components/Player/PlayerElements/PlayerAvatar";
import {useSelector} from "react-redux";
import {getRoundsById} from "modules/selectors/rounds";
import {getSquadsByIds, getPlayerByIdSelector} from "modules/selectors";
import {getMatchByIdSelector} from "modules/selectors/units";
import type {IPredictionsHistory, IUserProfilePrediction} from "modules/actions/leaderboards";
import {css} from "@mui/material";
import {useTranslation} from "react-i18next";
import styled from "@emotion/styled";
import {StreakBadge} from "components/StreakBadge";
import {buildStreakStatus, DayStatus} from "modules/utils/streak";
import {defaultTo, filter} from "lodash-es";
import {getMatchUpPrefix} from "modules/utils/match";

const Row = styled.div`
	background: linear-gradient(90deg, #42474d 0%, #282f38 100%);
	border-radius: 8px;
	padding: 12px;

	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
`;

const PredictionsInfo = styled.div`
	display: flex;
	flex-flow: column;
	gap: 10px;
`;

const PickInfo = styled.div<{result?: PredictionResult | null}>`
	display: flex;
	flex-flow: column;
	gap: 2px;
	span {
		font-weight: 400;
		font-size: 12px;
		line-height: 100%;
	}
	p {
		font-weight: 700;
		font-size: 14px;
		line-height: 130%;
		${(props) =>
			props.result &&
			{
				[PredictionResult.HIT]: css`
					color: #a3dd9e;
				`,
				[PredictionResult.NOT_HIT]: css`
					color: #fe6060;
				`,
				[PredictionResult.VOID]: css`
					color: #848990;
				`,
				[PredictionResult.USED_MULLIGAN]: css`
					color: #848990;
				`,
			}[props.result]}
	}
`;

const AvatarsWrapper = styled.div`
	position: relative;
`;
export const Avatars = styled.div`
	display: flex;
`;

interface IBackgroundAvatar {
	isOnePickOnly?: boolean;
	noMargins?: boolean;
}

const AvatarImg = styled.div<IBackgroundAvatar>`
	width: 44px;
	height: 44px;
	border-radius: 50%;
	background: #fff;
	overflow: hidden;
	box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
	img {
		width: 100%;
		height: 100%;
	}
	&:first-of-type {
		position: relative;
		z-index: 2;
		${(props) => (props.isOnePickOnly ? "" : "transform: translateX(8px);")}
	}
`;

interface ISelectResult {
	isOnePickOnly: boolean;
}

export const SelectResult = styled.div<ISelectResult>`
	position: absolute;
	left: -12px;
	top: 50%;
	z-index: 3;
	transform: translateY(-50%);
	${(props) =>
		!props.isOnePickOnly &&
		css`
			transform: translateX(8px) translateY(-50%);
		`}
`;

const ListDateTitle = styled.div`
	font-size: 18px;
	line-height: 1.3;
	font-weight: 700;
	padding-bottom: 8px;

	@media (max-width: 880px) {
		font-size: 16px;
		padding-bottom: 12px;
	}
`;

interface IProps {
	pick: IPredictionsHistory;
}

const PickHistoryRow: React.FC<IProps> = ({pick}) => {
	const i18n = useTranslation();
	const roundsById = useSelector(getRoundsById);
	const squadsByIdsSelector = useSelector(getSquadsByIds);
	const matchesSelector = useSelector(getMatchByIdSelector);
	const playerById = useSelector(getPlayerByIdSelector);
	const round = roundsById[pick.roundId];
	const matchSelector = useSelector(getMatchByIdSelector);
	const isToday = moment(round.date).isSame(moment(), "day");
	const isOnePickOnly = pick.roundPredictions.length === 1;
	const resultStatus = buildStreakStatus(pick.roundPredictions, matchSelector);
	const isPassResult = resultStatus === DayStatus.CONTINUES;

	const getScoreString = (prediction: IUserProfilePrediction) =>
		`${defaultTo(prediction?.hits, 0)} - ${defaultTo(prediction?.atBats, 0)}`;

	return (
		<Fragment>
			<ListDateTitle
				id={`${round.id}-title-picks`}
				aria-label={`Result for ${moment(round.date).format("dddd MMMM DD")}`}
			>
				{isToday
					? i18n.t("user_profile.picks.today", "Today")
					: moment(round.date).format("ddd M/D")}{" "}
			</ListDateTitle>

			<Row>
				<PredictionsInfo>
					{pick.roundPredictions.map((prediction) => {
						const player = playerById(prediction.playerId || 0);
						const match = matchesSelector(prediction.unitId);
						const [homeSquad, awaySquad] = squadsByIdsSelector([
							match?.homeSquadId,
							match?.awaySquadId,
						]);

						const isHome = player?.squadId === match?.homeSquadId;

						const opponentSquad = isHome ? awaySquad : homeSquad;

						const prefix = getMatchUpPrefix(isHome);

						return (
							<PickInfo
								key={`${prediction.unitId}-${prediction.playerId}`}
								result={prediction.result}
							>
								<span aria-label={"Pick status"}>
									{prediction.number === 1
										? i18n.t("user_profile.picks.picked", "Picked")
										: i18n.t("user_profile.picks.dd", "DD")}
								</span>
								<div>
									{player ? (
										<Fragment>
											<p>{player.name}</p>
											<p>
												{isPassResult
													? i18n.t("user_profile.picks.pass", "Pass")
													: getScoreString(prediction)}
												{` ${prefix} `}
												{opponentSquad && opponentSquad?.abbreviation}
											</p>
										</Fragment>
									) : (
										<p>
											{i18n.t(
												"user_profile.picks.hidden_dd",
												"batter hidden"
											)}
										</p>
									)}
								</div>
							</PickInfo>
						);
					})}
				</PredictionsInfo>

				<AvatarsWrapper>
					<SelectResult isOnePickOnly={isOnePickOnly} aria-label={"Streak value"}>
						<StreakBadge
							pick={{
								result: resultStatus,
								streak: pick.streak,
							}}
						/>
					</SelectResult>

					<Avatars aria-label={"player avatars"}>
						{filter(pick.roundPredictions, "playerId").map((prediction) => {
							const playerId = prediction.playerId || 0;
							const player = playerById(playerId);

							return (
								<AvatarImg
									key={playerId + prediction.unitId}
									isOnePickOnly={isOnePickOnly}
								>
									<PlayerImage type={PlayerImageType.HEADSHOT} player={player} />
								</AvatarImg>
							);
						})}
					</Avatars>
				</AvatarsWrapper>
			</Row>
		</Fragment>
	);
};

export default PickHistoryRow;
